<template>
  <div :class="$style.main">
    <ProductLabel :product="product" />

    <div :class="$style.product_title_box">
      <component :is="isProductPage ? 'h1' : 'h2'" :class="$style.product_title">
        {{ product.name }}
        <span v-if="product.material" :class="$style.material">{{ product.material }}</span>
      </component>
      <AtomsConsumerButton
        v-if="!product.not_display_product_list && pageType !== 'lpnh'"
        :class="$style.fav"
        @click="$emit('onFavorite')"
      >
        <AtomsConsumerImage :src="alreadyFavorite ? '/images/icon/fav-navy.svg' : '/images/icon/fav.svg'" />
      </AtomsConsumerButton>
    </div>

    <div :class="$style.price_container">
      <MoleculesConsumerCartPrice :sku="selectedSku"></MoleculesConsumerCartPrice>

      <div :class="$style.tips">
        <p v-if="isConfirm && giftWrappingsTotalPrice !== 0">
          {{ giftWrappingLabel }}費用 ＋￥{{ giftWrappingsTotalPrice.toLocaleString() }}（参考税込）
        </p>
        <p v-else-if="isConfirm && giftWrappingsTotalPrice === 0">(ラッピング費用 無料)</p>
        <p v-if="isEmbroideryConfirm">刺繍費用 ＋￥{{ selectEmbroidery.price.toLocaleString() }}（参考税込）</p>
      </div>
    </div>
    <div v-if="isDisplayRelatedTabs" :class="$style.segmented_control">
      <MoleculesConsumerSegmentedControl
        v-model="localProductId"
        :list="productSegmentList"
        @on-select-item="$emit('fetchProduct')"
      />
    </div>
    <div v-if="isDisplayRelatedLinks" :class="$style.related_links">
      <div v-for="(link, linkIndex) in listParts" :key="linkIndex">
        <AtomsConsumerLink :url="link.url" :class="$style.link_text">{{ link.title }}</AtomsConsumerLink>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { CartGift } from "@tential/ec-gql-schema/models/order"
import { PagePartsFragmentFragment } from "~/types/pageParts"
import { EmbroiderySelect } from "~/types/embroidery"
import { PagePartsProductType } from "~/types/product"
import { SkuFragmentFragment } from "~/types/type.generated"

const props = defineProps<{
  product: PagePartsProductType
  listParts: PagePartsFragmentFragment["list"]
  subtitle: string
  giftWrappingLabel: string
  isConfirm: boolean
  isEmbroideryConfirm: boolean
  pageType: string
  selectedGift: CartGift
  alreadyFavorite: boolean
  selectedSku: SkuFragmentFragment | null
  selectEmbroidery: EmbroiderySelect
  productRefId: string
  productSegmentList: { key: string; value: string }[]
}>()

const route = useRoute()

const isProductPage = computed(() => {
  const productRoutes = ["categorySlug-subCategorySlug-productSlug", "products-productSlug"]
  return productRoutes.includes(route.name?.toString() || "")
})

const emit = defineEmits<{
  (e: "onFavorite"): void
  (e: "fetchProduct"): void
  (e: "update:productRefId", productRefId: string): void
}>()

const localProductId = computed({
  get: () => props.productRefId,
  set: (value) => emit("update:productRefId", value),
})

const giftWrappingsTotalPrice = computed(() => {
  return props.selectedGift.price + props.selectedGift.message_price + props.selectedGift.card_price
})

const isDisplayRelatedLinks = computed(() => {
  return props.listParts.some((parts) => parts.title && parts.url)
})

const isDisplayRelatedTabs = computed(() => {
  return props.listParts.some((parts) => parts.subtitle && parts.product_id)
})
</script>

<style scoped module lang="scss">
.main {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;

  .product_title_box {
    display: flex;
    justify-content: space-between;
    .product_title {
      font-size: $font-size-15;
      gap: 0.25rem;
      display: flex;
      display: flex;
      flex-direction: column;
      @include lg {
        font-size: $font-size-20;
      }
    }
    .material {
      font-size: $font-size-12;
      display: block;
      @include lg {
        font-size: $font-size-14;
      }
    }
    a {
      color: $primary;
      text-decoration: underline;
    }
    p {
      font-size: $font-size-12;
      @include lg {
        font-size: $font-size-14;
      }
    }
  }

  .price_container {
    .tax_in_price {
      display: flex;
      align-items: flex-end;
      gap: 0.75rem;
      .price {
        @include main-font;
        font-size: $font-size-20;
        .label {
          font-size: $font-size-10;
        }
        &.discount {
          color: $danger;
        }
      }

      .compare {
        @include main-font;
        display: flex;
        position: relative;
        align-items: flex-end;
        font-size: $font-size-15;
        .label {
          font-size: $font-size-9;
        }
        &::after {
          content: "";
          display: block;
          width: 100%;
          height: 1px;
          background-color: $black;
          position: absolute;
          top: 50%;
          left: 0;
        }
      }
    }

    .tips {
      p {
        font-size: $font-size-10;
        @include lg {
          font-size: $font-size-12;
        }
      }
    }
  }
  .segmented_control {
    margin-top: 0.5rem;
    padding-top: 1rem;
    border-top: 1px solid $primary-20;
  }
  .related_links {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    margin-top: 0.5rem;

    .link_text {
      text-decoration: underline;
      font-size: $font-size-14;
    }
  }
}
</style>
